import React, { useState } from 'react';
import { AlertCircle, Key, ExternalLink, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { manualQuotaReset } from '../../services/openaiService';

const FinancialAnalysisErrorRecovery = ({ error, onRetry }) => {
  const [isTestingKey, setIsTestingKey] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showDetailedSteps, setShowDetailedSteps] = useState(false);

  const handleTestConnection = async () => {
    setIsTestingKey(true);
    setTestResult(null);
    
    try {
      // Reset quota state and get current status
      const status = manualQuotaReset();
      
      // Simulate a quick validation test
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      if (status?.configured && status?.status === 'available') {
        setTestResult({ success: true, message: 'API key validation successful' });
        setTimeout(() => {
          onRetry?.();
        }, 2000);
      } else {
        setTestResult({ 
          success: false, 
          message: status?.apiKeyValidation?.message || 'API key validation failed' 
        });
      }
    } catch (error) {
      setTestResult({ success: false, message: 'Connection test failed' });
    } finally {
      setIsTestingKey(false);
    }
  };

  const getErrorInfo = () => {
    const errorType = error?.errorDetails?.type || 'unknown';
    const errorContext = error?.errorDetails?.context || {};
    
    switch (errorType) {
      case 'invalid_key':
        return {
          title: 'Invalid OpenAI API Key',
          description: errorContext?.message || 'Your OpenAI API key is invalid or expired',
          urgency: 'high',
          icon: XCircle,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200'
        };
      case 'quota_exceeded': case'insufficient_quota':
        return {
          title: 'OpenAI Quota Exceeded',
          description: 'Your OpenAI usage quota has been exceeded',
          urgency: 'high',
          icon: AlertCircle,
          color: 'text-orange-600',
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200'
        };
      case 'placeholder_key':
        return {
          title: 'Placeholder API Key Detected',
          description: 'You need to replace the placeholder with your actual OpenAI API key',
          urgency: 'high',
          icon: Key,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200'
        };
      default:
        return {
          title: 'AI Service Unavailable',
          description: 'Financial analysis AI service is currently unavailable',
          urgency: 'medium',
          icon: AlertCircle,
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200'
        };
    }
  };

  const getRecoverySteps = () => {
    const errorContext = error?.errorDetails?.context || {};
    return errorContext?.recoverySteps || [
      'Check your OpenAI API key configuration',
      'Verify your OpenAI account status',
      'Ensure sufficient quota/billing is set up',
      'Try again after resolving the issue'
    ];
  };

  const errorInfo = getErrorInfo();
  const recoverySteps = getRecoverySteps();
  const ErrorIcon = errorInfo?.icon;

  return (
    <div className={`rounded-lg border ${errorInfo?.borderColor} ${errorInfo?.bgColor} p-6`}>
      <div className="flex items-start space-x-4">
        <div className={`flex-shrink-0 ${errorInfo?.color}`}>
          <ErrorIcon className="h-6 w-6" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <h3 className={`text-lg font-semibold ${errorInfo?.color}`}>
              {errorInfo?.title}
            </h3>
            {errorInfo?.urgency === 'high' && (
              <span className="px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded-full">
                Action Required
              </span>
            )}
          </div>
          
          <p className="text-gray-700 mb-4">
            {errorInfo?.description}
          </p>

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-3 mb-4">
            <button
              onClick={handleTestConnection}
              disabled={isTestingKey}
              className="flex items-center px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 border border-blue-200 rounded-md hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isTestingKey ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Testing Connection...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Test API Key
                </>
              )}
            </button>
            
            <a
              href="https://platform.openai.com/account/api-keys"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center px-4 py-2 text-sm font-medium text-green-700 bg-green-100 border border-green-200 rounded-md hover:bg-green-200 transition-colors"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              OpenAI Dashboard
            </a>
            
            <button
              onClick={() => setShowDetailedSteps(!showDetailedSteps)}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-md hover:bg-gray-200 transition-colors"
            >
              <Key className="w-4 h-4 mr-2" />
              {showDetailedSteps ? 'Hide' : 'Show'} Detailed Steps
            </button>
          </div>

          {/* Test Result */}
          {testResult && (
            <div className={`flex items-center p-3 rounded-md mb-4 ${
              testResult?.success 
                ? 'bg-green-50 border border-green-200 text-green-800' :'bg-red-50 border border-red-200 text-red-800'
            }`}>
              {testResult?.success ? (
                <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 mr-2 text-red-600" />
              )}
              <span className="text-sm font-medium">{testResult?.message}</span>
              {testResult?.success && (
                <span className="ml-2 text-xs text-green-600">Retrying analysis...</span>
              )}
            </div>
          )}

          {/* Detailed Recovery Steps */}
          {showDetailedSteps && (
            <div className="mt-4 p-4 bg-white border border-gray-200 rounded-md">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Recovery Steps:</h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                {recoverySteps?.map((step, index) => (
                  <li key={index} className="leading-relaxed">
                    {step}
                  </li>
                ))}
              </ol>
              
              {/* Environment Variable Hint */}
              <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                <h5 className="text-xs font-semibold text-gray-800 mb-1">Environment Variable:</h5>
                <code className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                  VITE_OPENAI_API_KEY=your-actual-api-key-here
                </code>
                <p className="text-xs text-gray-600 mt-1">
                  Make sure to restart your development server after updating the .env file
                </p>
              </div>
            </div>
          )}

          {/* Additional Context */}
          {error?.errorDetails?.context?.apiKeyFormat && (
            <div className="mt-4 text-xs text-gray-600">
              <strong>Detected Key Format:</strong> {error?.errorDetails?.context?.apiKeyFormat}
              {error?.errorDetails?.context?.apiKeyFormat === 'project' && (
                <span className="ml-2 text-blue-600">(Recommended format)</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinancialAnalysisErrorRecovery;